import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimCreditsWithPin, submitCreditRequest } from '../../services/api';
import SessionManager from '../../utils/sessionManager';

const InsufficientCreditsModal = ({ 
  isOpen, 
  onClose, 
  requiredCredits, 
  availableCredits, 
  serviceName,
  onCreditsAdded 
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pin'); // 'pin' or 'request'
  const [pin, setPin] = useState('');
  const [requestAmount, setRequestAmount] = useState(100);
  const [requestReason, setRequestReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const session = SessionManager.getSession('candidate');
      if (!session?.profile?.id) {
        throw new Error('Please log in to continue');
      }

      const result = await claimCreditsWithPin(session.profile.id, pin);
      setSuccess(result.message);
      
      // Notify parent and trigger balance refresh
      if (onCreditsAdded) {
        onCreditsAdded(result.new_balance);
      }
      window.dispatchEvent(new CustomEvent('creditBalanceUpdated'));
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
        setPin('');
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Invalid PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const session = SessionManager.getSession('candidate');
      if (!session?.profile?.id) {
        throw new Error('Please log in to continue');
      }

      const result = await submitCreditRequest(
        session.profile.id,
        requestAmount,
        requestReason || `Need credits for ${serviceName}`
      );
      
      setSuccess('Request submitted! You will be notified when admin processes your request.');
      
      // Close modal after delay
      setTimeout(() => {
        onClose();
        setRequestAmount(100);
        setRequestReason('');
        setSuccess('');
      }, 2500);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Insufficient Credits</h2>
              <p className="text-white text-opacity-90 text-sm">
                Need {requiredCredits} credits, have {availableCredits}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You need <span className="font-semibold text-red-600">{requiredCredits - availableCredits} more credits</span> to use {serviceName}.
          </p>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => navigate('/payment')}
              className="flex-1 py-2 text-sm font-medium border-b-2 border-transparent text-emerald-600 hover:text-emerald-700 hover:border-emerald-200 transition-colors"
            >
              💳 Buy Credits
            </button>
            <button
              onClick={() => setActiveTab('pin')}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pin'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Enter Coupon
            </button>
            {/* Request Credits tab hidden
            <button
              onClick={() => setActiveTab('request')}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'request'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Request Credits
            </button>
            */}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {success}
            </div>
          )}

          {/* PIN Tab */}
          {activeTab === 'pin' && (
            <form onSubmit={handlePinSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Coupon Code
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter coupon code to claim credits"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a valid coupon code to instantly claim credits
                </p>
              </div>
              <button
                type="submit"
                disabled={loading || !pin}
                className="w-full py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Processing...' : 'Claim Credits'}
              </button>
            </form>
          )}

          {/* Request Tab - hidden for now
          {activeTab === 'request' && (
            <form onSubmit={handleRequestSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credits Needed
                </label>
                <input
                  type="number"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(parseInt(e.target.value) || 0)}
                  min="10"
                  max="1000"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (Optional)
                </label>
                <textarea
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder="Why do you need credits?"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading || requestAmount < 10}
                className="w-full py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Admin will review and approve your request
              </p>
            </form>
          )}
          */}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsufficientCreditsModal;
